import { HubItemCategoryIdentifierModel, HubTileLayoutKind } from "client/Api/Hub/common";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import {
	DisplayCarouselLayout,
	DisplayGridLayout,
	DisplayListLayout,
	HubTileDefaultViewKind
} from "client/Api/Workspace/workspace";
import { TimeZone } from "client/DateAndTime/timezones";
import { getHubUrlForEvent } from "client/Features/Hub/Events/EventsPage";
import { getHubUrlForKnowledgeItem } from "client/Features/Hub/KnowledgeItems/KnowledgeItemsPage";
import { getHubUrlForAnnouncement } from "client/Features/Hub/Announcements/AnnouncementsPage";
import { never } from "client_legacy/ndaw/errors";
import { Guid, newGuid } from "client_legacy/ndaw/guid";
import { navigateTo } from "client_legacy/ndaw/navigator";
import { AnnouncementsLiveInformationProvider } from "client_legacy/Workspace/LiveInformation/Hub/announcementProvider";
import { EventLiveInformationProvider } from "client_legacy/Workspace/LiveInformation/Hub/eventProvider";
import { KnowledgeItemLiveInformationProvider } from "client_legacy/Workspace/LiveInformation/Hub/knowledgeItemProvider";
import { DimensionToRender } from "../../RenderState/common";
import {
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import { AnnouncementListItem } from "./ListItem/AnnouncementListItem";
import { AnnouncementCard } from "./Card/AnnouncementCard";
import { KnowledgeItemListItem } from "./ListItem/KnowledgeItemListItem";
import { KnowledgeItemCard } from "./Card/KnowledgeItemCard";
import { EventListItem } from "./ListItem/EventListItem";
import { EventCard } from "./Card/EventItemCard";
import { LiveTileContent } from "../LiveTileContent";
import { AppImageDisplayInfo } from "client/Api/AppIcon/common";
import { getCurrentCulture, localize } from "client_legacy/ndaw/culture";
import { TileTabProps } from "../TileTabSelector";
import {
	CardProps,
	HubTileCategoryIds,
	ListItemProps
} from "./common";
import { HubCardSizeInformation, HubItemLiveInformationProvider } from "./hooks";
import { ListLayout } from "./ListLayout";
import { CarouselLayout } from "./CarouselLayout";
import { GridLayout } from "./GridLayout";
import { sortBy } from "client_legacy/ndaw/arrayHelpers";
import { HubItemTileFooter } from "./HubItemTileFooter";
import { HubDataSource } from "client/Api/Hub/hub";
import { hubItemKindToHubDataSourceMap } from "client/Features/Hub/Sidebar/common";
import { isNotNil } from "client/Misc/utils";
import { Action, ActionKind } from "client_legacy/ndaw/actions";

export type CategoryViewProps = {
	tileId: number;
	tileName: string;
	icon: AppImageDisplayInfo;
	dimension: DimensionToRender;
	environmentAppId: number;
	layout: | DisplayCarouselLayout
		| DisplayListLayout
		| DisplayGridLayout;
	hideHeader: boolean;
	hideFooter: boolean;
	announcementProvider: AnnouncementsLiveInformationProvider;
	knowledgeItemProvider: KnowledgeItemLiveInformationProvider;
	eventProvider: EventLiveInformationProvider;
	timezone: TimeZone;
	onAddAnalyticsEvent: () => void;
	onCreateNewItem: (itemKind: HubItemKind, preferredCategoryId?: number | null) => void;
};

export const CategoryView = (props: CategoryViewProps): ReactElement => {
	const {
		tileId,
		tileName,
		icon,
		dimension,
		layout,
		hideHeader,
		hideFooter,
		announcementProvider,
		knowledgeItemProvider,
		eventProvider,
		timezone,
		onAddAnalyticsEvent,
		onCreateNewItem
	} = props;

	const [activeTabId, setActiveTabId] = useState<Guid | undefined>();
	const [tabs, setTabs] = useState<readonly HubTab[]>([]);

	const canCreateNewItems = useMemo(
		(): boolean => tabs.some(x => x.canCreateNewItem),
		[tabs]
	);

	const activeTab = useMemo(
		(): HubTab | undefined => tabs.find(x => x.uniqueId === activeTabId),
		[activeTabId, tabs]
	);

	const { itemKind, defaultView } = layout;

	const categoriesProvider = useMemo(
		(): CategoriesProvider => {
			switch (itemKind) {
				case HubItemKind.Announcement:
					return announcementProvider;
				case HubItemKind.KnowledgeItem:
					return knowledgeItemProvider;
				case HubItemKind.Event:
					return eventProvider;
				default:
					never(itemKind);
			}
		},
		[
			itemKind,
			announcementProvider,
			knowledgeItemProvider,
			eventProvider
		]
	);

	useEffect(
		(): void => {
			const load = async (): Promise<void> => {
				const categories: readonly HubItemCategoryIdentifierModel[] = await categoriesProvider.listCategories();

				const tabs: readonly HubTab[] = categories.map((x): HubTab => ({
					categoryIds: isNotNil(x.categoryId)
						? [x.categoryId]
						: null,
					canCreateNewItem: x.canCreateNewItem,
					name: x.name,
					uniqueId: newGuid(),
					count: x.newItemsCount
				}));

				const customDefaultViewTab: HubTab | undefined = defaultView.kind === HubTileDefaultViewKind.CategoryCollection
					? getCustomDefaultViewTab(
						defaultView.categoryIds,
						categories
					)
					: undefined;

				setTabs(
					customDefaultViewTab
						? [
							customDefaultViewTab,
							...tabs
						]
						: tabs
				);

				const allCategoriesTabId: Guid | undefined = tabs.find((x) => x.categoryIds === null)
					?.uniqueId;
				let tabIdToActivate: Guid | undefined;

				switch (defaultView.kind) {
					case HubTileDefaultViewKind.AllItems:
						tabIdToActivate = allCategoriesTabId;
						break;
					case HubTileDefaultViewKind.Category:
						tabIdToActivate = tabs
							.find(
								x => {
									return x.categoryIds?.length === 1
										&& x.categoryIds[0] === defaultView.categoryId;
								}
							)
							?.uniqueId;
						break;
					case HubTileDefaultViewKind.CategoryCollection:
						tabIdToActivate = customDefaultViewTab?.uniqueId;
						break;
					default:
						never(defaultView);
				}

				if(!tabIdToActivate) {
					tabIdToActivate = allCategoriesTabId;
				}

				if (!tabIdToActivate && tabs.length > 0) {
					tabIdToActivate = tabs[0]?.uniqueId;
				}

				setActiveTabId(tabIdToActivate);
			};

			load();
		},
		[
			categoriesProvider,
			defaultView
		]
	);

	const onOpen = useCallback(
		(id: number): void => {
			onAddAnalyticsEvent();

			const source: HubDataSource = hubItemKindToHubDataSourceMap[itemKind];

			let url: string;
			switch (source) {
				case HubDataSource.Announcement:
					url = getHubUrlForAnnouncement({
						source: HubDataSource.Announcement,
						categoryId: getCategoryIdToNavigate(activeTab),
						itemId: id
					});
					break;
				case HubDataSource.KnowledgeItem:
					url = getHubUrlForKnowledgeItem({
						source: HubDataSource.KnowledgeItem,
						itemId: id,
						viewIdentifier: {
							kind: "categoryId",
							categoryId: getCategoryIdToNavigate(activeTab)
						}
					});
					break;
				case HubDataSource.Event:
					url = getHubUrlForEvent({
						source: HubDataSource.Event,
						categoryId: getCategoryIdToNavigate(activeTab),
						itemId: id
					});
					break;
				default:
					never(source);
			}

			navigateTo(url);
		},
		[
			onAddAnalyticsEvent,
			activeTab,
			itemKind
		]
	);

	const defaultActions = useMemo(
		(): readonly Action<ActionContext>[] => listDefaultActions(
			{
				new: (itemKind) => onCreateNewItem(
					itemKind,
					activeTab?.categoryIds?.[0]
				)
			}
		),
		[
			activeTab?.categoryIds,
			onCreateNewItem
		]
	);

	const actionContext = useMemo(
		(): ActionContext => ({
			canCreateNewItems,
			itemKind
		}),
		[canCreateNewItems, itemKind]
	);

	const renderContent = (): ReactElement | null => {
		if (!activeTab) {
			return null;
		}

		const isAutoPlayEnabled: boolean = layout.kind === HubTileLayoutKind.Carousel
			? layout.isAutoPlayEnabled
			: false;

		return (
			<>
				{itemKind === HubItemKind.Announcement && (
					<LayoutRenderer
						layout={layout.kind}
						isAutoPlayEnabled={isAutoPlayEnabled}
						tabId={activeTab.uniqueId}
						categoryIds={activeTab.categoryIds}
						dimension={dimension}
						provider={announcementProvider}
						listInterestsFactory={(isWideTile) => ({
							items: true as const,
							comments: isWideTile,
							reactions: isWideTile,
							lastPinnedItem: true
						})}
						listItemComponent={AnnouncementListItem}
						cardInterestsFactory={() => ({
							items: true as const,
							comments: true,
							reactions: true,
							lastPinnedItem: true
						})}
						cardComponent={AnnouncementCard}
						onOpen={onOpen}
						timezone={timezone}
					/>
				)}

				{itemKind === HubItemKind.KnowledgeItem && (
					<LayoutRenderer
						layout={layout.kind}
						isAutoPlayEnabled={isAutoPlayEnabled}
						tabId={activeTab.uniqueId}
						categoryIds={activeTab.categoryIds}
						dimension={dimension}
						provider={knowledgeItemProvider}
						listInterestsFactory={() => ({
							items: true,
							lastPinnedItem: true
						})}
						listItemComponent={KnowledgeItemListItem}
						cardInterestsFactory={() => ({
							items: true,
							lastPinnedItem: true
						})}
						cardComponent={KnowledgeItemCard}
						onOpen={onOpen}
						timezone={timezone}
					/>
				)}

				{itemKind === HubItemKind.Event && (
					<LayoutRenderer
						layout={layout.kind}
						isAutoPlayEnabled={isAutoPlayEnabled}
						tabId={activeTab.uniqueId}
						categoryIds={activeTab.categoryIds}
						dimension={dimension}
						provider={eventProvider}
						listItemComponent={EventListItem}
						listInterestsFactory={() => ({
							items: true as const
						})}
						cardInterestsFactory={() => ({
							items: true as const,
						})}
						cardComponent={EventCard}
						onOpen={onOpen}
						timezone={timezone}
					/>
				)}
			</>
		);
	};

	return (
		<>
			{!hideHeader && (
				<LiveTileContent.Header
					icon={icon}
					actions={defaultActions}
					actionContext={actionContext}
				>
					<LiveTileContent.TabSelector
						value={activeTabId}
						tabs={tabs}
						onChange={setActiveTabId}
						tileId={tileId}
						tileName={tileName}
					/>
				</LiveTileContent.Header>
			)}

			<LiveTileContent.Content>
				<LiveTileContent.ContentStack>
					{renderContent()}
				</LiveTileContent.ContentStack>
			</LiveTileContent.Content>

			{!hideFooter && (
				<HubItemTileFooter
					categoryId={getCategoryIdToNavigate(activeTab)}
					kind={itemKind}
					onAddAnalyticsEvent={onAddAnalyticsEvent}
					categoryName={activeTab?.name}
				/>
			)}
		</>
	);
};
CategoryView.displayName = "CategoryView";

type HubTab = TileTabProps & {
	categoryIds: HubTileCategoryIds;
	canCreateNewItem: boolean;
};

type LayoutRendererProps<TItem extends { id: number; }, TInterests> = {
	layout: Exclude<HubTileLayoutKind, HubTileLayoutKind.Spotlight | HubTileLayoutKind.Feed>;
	isAutoPlayEnabled: boolean;
	tabId: Guid;
	categoryIds: HubTileCategoryIds;
	dimension: DimensionToRender;
	provider: HubItemLiveInformationProvider<TItem, TInterests>;
	listInterestsFactory: (isWideTile: boolean) => Required<TInterests>;
	listItemComponent: {
		(props: ListItemProps<TItem>): ReactElement;
	};
	cardInterestsFactory: (sizeInfo: HubCardSizeInformation) => TInterests;
	cardComponent: {
		(props: CardProps<TItem>): ReactElement;
	};
	onOpen: (id: number) => void;
	timezone: TimeZone;
};

const LayoutRenderer = <TItem extends { id: number; }, TInterests>(
	props: LayoutRendererProps<TItem, TInterests>
): ReactElement => {
	const {
		layout,
		isAutoPlayEnabled,
		tabId,
		categoryIds,
		dimension,
		provider,
		listInterestsFactory,
		listItemComponent,
		cardInterestsFactory,
		cardComponent,
		onOpen,
		timezone
	} = props;

	switch (layout) {
		case HubTileLayoutKind.List:
			return (
				<ListLayout
					tabId={tabId}
					categoryIds={categoryIds}
					dimension={dimension}
					provider={provider}
					interestsFactory={listInterestsFactory}
					listItemComponent={listItemComponent}
					onOpen={onOpen}
					timezone={timezone}
				/>
			);
		case HubTileLayoutKind.Carousel:
			return (
				<CarouselLayout
					tabId={tabId}
					categoryIds={categoryIds}
					dimension={dimension}
					provider={provider}
					interestsFactory={cardInterestsFactory}
					cardComponent={cardComponent}
					onOpen={onOpen}
					timezone={timezone}
					isAutoPlayEnabled={isAutoPlayEnabled}
				/>
			);
		case HubTileLayoutKind.Grid:
			return (
				<GridLayout
					tabId={tabId}
					categoryIds={categoryIds}
					dimension={dimension}
					provider={provider}
					interestsFactory={cardInterestsFactory}
					cardComponent={cardComponent}
					onOpen={onOpen}
					timezone={timezone}
				/>
			);
		default:
			never(layout);
	}
};
LayoutRenderer.displayName = "LayoutRenderer";

type CategoriesProvider = {
	listCategories(): Promise<readonly HubItemCategoryIdentifierModel[]>;
	forceReload(): void;
};

type CustomCategory = Omit<HubItemCategoryIdentifierModel , "categoryId"> & {
	categoryId: number;
};

function getCustomDefaultViewTab(
	customCategoryIds: readonly number[],
	categories: readonly HubItemCategoryIdentifierModel[]
): HubTab | undefined {
	const result: CustomCategory[] = [];
	let totalNewItemsCount: number = 0;

	for (const customCategoryId of customCategoryIds) {
		const category: HubItemCategoryIdentifierModel | undefined = categories.find(
			x => x.categoryId === customCategoryId
		);

		if (!category) {
			continue;
		}

		const {
			name,
			canCreateNewItem,
			newItemsCount
		} = category;

		totalNewItemsCount += newItemsCount;

		result.push({
			categoryId: customCategoryId,
			name,
			canCreateNewItem,
			newItemsCount
		});
	}

	if (result.length === 0) {
		return undefined;
	}

	return {
		categoryIds: result.map(x => x.categoryId),
		name: getName(result.map(x => x.name)),
		uniqueId: newGuid(),
		canCreateNewItem: result
			.map(x => x.canCreateNewItem)
			.some(x => x),
		count: totalNewItemsCount
	};
}

function getName(
	names: readonly string[]
): string {
	const result: string[] = sortBy(names, x => x)
		.slice(0, 3);

	if (names.length > 3) {
		result.push("...");
	}

	const currentCulture: string = getCurrentCulture();

	const formattedNames: string = new Intl
		.ListFormat(
			currentCulture,
			{
				style: "short",
				type: "unit"
			}
		)
		.format(result);

	return formattedNames;
}

function getCategoryIdToNavigate(
	activeTab: HubTab | undefined
): number | undefined {
	if (!activeTab) {
		return undefined;
	}

	const {
		categoryIds
	} = activeTab;

	return categoryIds?.length === 1
		? categoryIds[0]
		: undefined;
}

export type ActionContext = {
	itemKind: HubItemKind;
	canCreateNewItems: boolean;
};

export type ActionHandlers = {
	new: (itemKind: HubItemKind) => void;
};

export function listDefaultActions(
	handlers: ActionHandlers
): readonly Action<ActionContext>[] {

	return [
		{
			kind: ActionKind.Regular,
			name: "new-item",
			title: (c) => localize(itemKindToNewItemKeyMap[c.itemKind]),
			icon: "plus",
			canExecute: (x) => x.canCreateNewItems,
			execute: (c) => handlers.new(c.itemKind)
		}
	];
}

const itemKindToNewItemKeyMap: { [key in HubItemKind]: LocalizationKey; } = {
	[HubItemKind.Announcement]: "_NewAnnouncement",
	[HubItemKind.KnowledgeItem]: "_NewKnowledgeArticle",
	[HubItemKind.Event]: "_NewEvent"
};
